import * as React from 'react';
import { Formik, FormikConfig } from 'formik';
import { Redirect } from 'react-router-dom';
import { makeValidator } from 'mandle';
import {
  FullMatchFragment,
  useUpdateMatchMutation,
} from '../../generated/graphql';
import { makeInputPropsMaker } from '../../utils/inputPropsMaker';
import { Title } from '../../components/Title';
import { SubmitButton } from '../../components/SubmitButton';
import { TextArea } from '../../components/TextArea';
import { paths } from '../../paths';
import { useAlert } from '../../contexts/AlertContext';
import { useAnalytics } from '../../contexts/AnalyticsContext';
import { formatErrors } from '../../utils/formatErrors';
import { ButtonLink } from '../../components/Button';
import { Heading } from '../../components/Heading';
import { maxLength } from '../../validators';

interface Props {
  match: Pick<FullMatchFragment, 'id' | 'matchReport' | 'opponent'>;
}

interface Fields {
  matchReport: string;
}

const validate = makeValidator<Fields>({
  matchReport: [maxLength(65000, 'Match report is too long')],
});

export const CreateMatchReport: React.FC<Props> = ({ match }) => {
  const initialValues: Fields = { matchReport: match.matchReport || '' };
  const alert = useAlert();
  const analytics = useAnalytics();
  const formEventMeta = { form: 'update_match_report' };

  const [updateMatchResponse, updateMatch] = useUpdateMatchMutation();

  const onSubmit: FormikConfig<Fields>['onSubmit'] = (values, actions) => {
    updateMatch({
      id: match.id,
      input: { matchReport: values.matchReport },
    }).then((response) => {
      if (response.data?.updateMatch?.validation) {
        analytics.track('Data Submit Failed', formEventMeta);
        return actions.setErrors(
          formatErrors(response.data.updateMatch.validation),
        );
      }
      if (response.data?.updateMatch?.entity) {
        analytics.track('Data Submit Succeeded', formEventMeta);
        alert('Match updated 🎉');
      }
    });
  };

  if (updateMatchResponse.data?.updateMatch?.entity) {
    return <Redirect to={paths.match.home(match.id)} />;
  }

  return (
    <Formik
      initialValues={initialValues}
      validate={validate}
      onSubmit={onSubmit}
    >
      {(formProps) => {
        const getInputProps = makeInputPropsMaker(formProps);

        return (
          <div className="space-y-4">
            <Title>Match Report</Title>
            <Heading level={3} className="text-lg">
              Match vs {match.opponent}
            </Heading>
            <p>How did the match go? What were the highlights?</p>
            <form
              noValidate={true}
              onSubmit={formProps.handleSubmit}
              className="space-y-6"
            >
              <div>
                <TextArea
                  label="Match Report"
                  hideLabel={true}
                  {...getInputProps('matchReport')}
                />
              </div>
              <SubmitButton loading={updateMatchResponse.fetching}>
                Submit
              </SubmitButton>
              <ButtonLink to={paths.match.home(match.id)} kind="plain">
                Cancel
              </ButtonLink>
            </form>
          </div>
        );
      }}
    </Formik>
  );
};
